import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Pencil, Trash2, Plus, CheckCircle2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import ExportButtons from './ExportButtons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';

const TypeSettingManager = () => {
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [sortBy, setSortBy] = useState('display_order');
  const [sortDirection, setSortDirection] = useState('asc');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    display_order: 0,
  });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchTrainingTypes();
  }, []);

  const fetchTrainingTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/training-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTrainingTypes(data.training_types || []);
      }
    } catch (error) {
      console.error('Error fetching training types:', error);
      toast({ title: 'Error', description: 'Failed to load training types', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateType = async () => {
    try {
      if (!formData.name) {
        toast({ title: 'Error', description: 'Please enter a type name', variant: 'destructive' });
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/training-types`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          display_order: parseInt(formData.display_order || 0)
        })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Training type created successfully' });
        setIsCreateModalOpen(false);
        resetForm();
        fetchTrainingTypes();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to create training type', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create training type', variant: 'destructive' });
    }
  };

  const handleUpdateType = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/training-types/${editingType.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          display_order: parseInt(formData.display_order || 0)
        })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Training type updated successfully' });
        setIsEditModalOpen(false);
        setEditingType(null);
        resetForm();
        fetchTrainingTypes();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to update training type', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update training type', variant: 'destructive' });
    }
  };

  const handleDeleteType = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/training-types/${typeToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Training type deleted successfully' });
        setDeleteConfirmOpen(false);
        setTypeToDelete(null);
        fetchTrainingTypes();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete training type', variant: 'destructive' });
    }
  };

  const openEditModal = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || '',
      display_order: type.display_order || 0,
    });
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      display_order: 0,
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const sortedTypes = [...trainingTypes].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    if (sortBy === 'display_order') {
      aVal = parseInt(aVal || 0);
      bVal = parseInt(bVal || 0);
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  if (loading) {
    return <div className="p-6">Loading training types...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Type Setting</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage training session types (Online, In Person, etc.)</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Type
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Types</div>
            <div className="text-3xl font-bold">{trainingTypes.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Active Types</div>
            <div className="text-3xl font-bold text-green-600">
              {trainingTypes.filter(t => t.active !== false).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Inactive Types</div>
            <div className="text-3xl font-bold text-gray-400">
              {trainingTypes.filter(t => t.active === false).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Buttons */}
      <ExportButtons 
        data={sortedTypes}
        columns={[
          { header: 'Type ID', key: 'id', accessor: (row) => row.id ? row.id.substring(0, 8) + '...' : 'N/A' },
          { header: 'Name', key: 'name', accessor: (row) => row.name || 'N/A' },
          { header: 'Description', key: 'description', accessor: (row) => row.description || '-' },
          { header: 'Display Order', key: 'display_order', accessor: (row) => row.display_order || 0 },
          { header: 'Status', key: 'active', accessor: (row) => row.active !== false ? 'Active' : 'Inactive' }
        ]}
        filename="training-type-settings"
      />

      {/* Types Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            All Training Types
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trainingTypes.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No training types yet. Add your first type!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('id')}
                    >
                      <div className="flex items-center gap-2">
                        Type ID
                        {sortBy === 'id' && (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-2">
                        Type Name
                        {sortBy === 'name' && (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('display_order')}
                    >
                      <div className="flex items-center gap-2">
                        Order
                        {sortBy === 'display_order' && (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortedTypes.map((type) => (
                    <tr key={type.id} className="hover:bg-gray-50 dark:bg-gray-700">
                      <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400">
                        {type.id.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">{type.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{type.description || '-'}</td>
                      <td className="px-4 py-3 text-sm">{type.display_order || 0}</td>
                      <td className="px-4 py-3">
                        <Badge variant={type.active !== false ? 'default' : 'secondary'}>
                          {type.active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(type)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setTypeToDelete(type);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={isCreateModalOpen || isEditModalOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setEditingType(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Training Type' : 'Add New Training Type'}</DialogTitle>
            <DialogDescription>
              {editingType ? 'Update training type details' : 'Add a new session type for trainers'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div>
              <Label>Type Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Online, In Person, Hybrid"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this training type"
                rows={3}
              />
            </div>

            <div>
              <Label>Display Order</Label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Lower numbers appear first
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreateModalOpen(false);
              setIsEditModalOpen(false);
              setEditingType(null);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={editingType ? handleUpdateType : handleCreateType}>
              {editingType ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Training Type</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{typeToDelete?.name}"? Trainers using this type may be affected. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteType}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TypeSettingManager;
